// Estado de una tabla POS: filtros, píldora y orden sobre los productos de la tienda.
import { useMemo, useRef, useState } from 'react';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import {
    DEFAULT_POS_SORT,
    nextPosSort,
    visiblePosItems,
    type PosSort,
    type PosSortKey,
} from './lib/posCatalog';
import {
    brandOptions,
    capacityOptions,
    DEFAULT_POS_FILTERS,
    filterPosItems,
    financierOptions,
    hasPromo,
    matchesPosStat,
    posStats,
    tierOptions,
    type IsPromoted,
    type PosFilters,
    type PosStat,
} from './lib/posFilters';
import type { PosFilterOptions } from './components/PosFiltersPanel';

/** `isPromoted` debe ser estable entre renders; por defecto, la marca de promo de la fila. */
export function usePosExplorer(
    items: CatalogItem[],
    isLastPieces: (item: CatalogItem) => boolean,
    isPromoted: IsPromoted = hasPromo,
) {
    const [filters, setFilters] = useState<PosFilters>(DEFAULT_POS_FILTERS);
    const [stat, setStat] = useState<PosStat>('all');
    const [sort, setSort] = useState<PosSort>(DEFAULT_POS_SORT);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const searchRef = useRef<HTMLInputElement>(null);

    const filtered = useMemo(
        () => filterPosItems(items, filters, isPromoted),
        [items, filters, isPromoted],
    );
    const stats = useMemo(
        () => posStats(filtered, isLastPieces, isPromoted),
        [filtered, isLastPieces, isPromoted],
    );
    const rows = useMemo(
        () =>
            visiblePosItems(
                filtered.filter((item) => matchesPosStat(item, stat, isLastPieces, isPromoted)),
                '',
                sort,
            ),
        [filtered, stat, sort, isLastPieces, isPromoted],
    );
    const options = useMemo<PosFilterOptions>(
        () => ({
            brands: brandOptions(items),
            financiers: financierOptions(items),
            tiers: tierOptions(items),
            capacities: capacityOptions(items),
        }),
        [items],
    );

    return {
        filters,
        setFilters,
        stat,
        setStat,
        sort,
        toggleSort: (key: PosSortKey) => {
            setSort((current) => nextPosSort(current, key));
        },
        filtersCollapsed,
        toggleFiltersCollapsed: () => {
            setFiltersCollapsed((value) => !value);
        },
        searchRef,
        rows,
        stats,
        options,
    };
}
